import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { rememberTenant, tenantHint, type ApiClient, type AppName, type PublicBranding, type SessionManager, type SessionState, type SignInAnswer } from '@despachador/api-client';
import { applyBranding, type Branding, errorText, ErrorState, setFormatContext } from '../index';
import { Activation } from './Activation';
import { ACTIVATION_PATH, captureActivationToken, clearActivationToken, subscribeActivationToken } from './activation-token';
import { ChallengeStep, type Challenge } from './ChallengeStep';
import { activationTrip, clearActivationTrip, clearGoogleReturn, GOOGLE_PATH, googleReturn, subscribeGoogleReturn, takeReturnPath } from './google-return';
import { GoogleLanding } from './GoogleLanding';
import { AuthCard, AuthLayoutContext, AuthSpinner, RecoveryCodes, type AuthBrand, type AuthLayout } from './pieces';
import { ReauthProvider } from './Reauth';
import { emptyRecovery, Recovery, RECOVERY_PATHS, type RecoveryState, type RecoveryStep } from './Recovery';
import { SignIn } from './SignIn';
import { isStatusMotive, MOTIVE_OF_REASON, StatusCard, StatusScreen, type StatusMotive } from './StatusScreen';

/** What `GET /auth/me` answers; each app may read more fields. */
export interface SessionMe {
  user: { id: string; email: string | null; phone: string; fullName: string; roles: string[]; driverId: string | null };
  permissions: string[];
  session: { id: string; audience: AppName; authenticatedAt: string };
  tenant: { id: string; currency: string; timezone: string; locale: string; mapCenter: { lat: number; lng: number } };
  branding: Branding & { tenantSlug: string } & Record<string, any>;
  features: Record<string, boolean>;
}

/** The app's navigation, so the gate owns `/acceso/*` inside the app's router without depending on it. */
export interface GateRouter {
  pathname: string;
  search: string;
  navigate: (to: string, opts?: { replace?: boolean }) => void;
}

export interface SessionGateProps<M extends SessionMe> {
  api: ApiClient;
  app: AppName;
  router: GateRouter;
  /** Shown after the brand on the sign-in screen and in the browser tab ("Operación", "Conductor"). */
  appName: string;
  subtitle: string;
  platform: 'WEB' | 'PWA';
  /** Bigger controls for touch-first apps. */
  large?: boolean;
  /** Layout of the screens without a session: the centered card (default) or the split screen. */
  layout?: AuthLayout;
  /** Whether this account belongs in this app; the API decides too (APP_NOT_ALLOWED). null = yes. */
  acceptAccount: (me: M) => StatusMotive | null;
  /** The public branding of this address (e.g. the driver app points its manifest to it). */
  onBranding?: (branding: Branding & { tenantSlug: string; brandName: string }) => void;
  /** Called on the sign-in tap (a user gesture). */
  onSignInGesture?: () => void;
  children: (session: { me: M; reload: () => Promise<void>; signOut: () => Promise<void> }) => ReactNode;
}

export const ACCESS_PREFIX = '/acceso/';
const SIGN_IN = '/acceso/ingresar';
const STEP = '/acceso/verificacion';
const STATUS = '/acceso/estado/';

/** Subscribes a component to the session state. */
export function useSessionState(session: SessionManager): SessionState {
  return useSyncExternalStore(
    (fn) => session.subscribe(fn),
    () => session.state,
  );
}

/** A path inside this app to go back to after signing in (never an external URL nor /acceso). */
function safeNext(raw: string | null): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\') || raw.startsWith(ACCESS_PREFIX)) return null;
  return raw;
}

const withNext = (path: string, next: string | null) => (next ? `${path}?next=${encodeURIComponent(next)}` : path);

/** A step of the recovery opened without its flow in memory (a reload): it starts over and says why. */
const RECOVERY_RESTART = `${RECOVERY_PATHS.start}?reinicio=1`;
const recoveryStepOf = (path: string): RecoveryStep | null => (Object.keys(RECOVERY_PATHS) as RecoveryStep[]).find((k) => RECOVERY_PATHS[k] === path) ?? null;

/**
 * The single start-up of every app (ADR-015 §10). It owns the session: checks it once, shows the
 * screens without a session as routes of the app (`/acceso/ingresar`, `/acceso/verificacion`,
 * `/acceso/estado/:motivo`, which work with the Back button, `/acceso/activar` for an invitation
 * link, `/acceso/recuperar…` for the password recovery and `/acceso/google` for Google's return),
 * loads the account, asks the app whether that account belongs here and only then renders the app. A session that ends anywhere (this tab, another tab, the server) takes the
 * person to the screen that says why.
 *
 * An invitation link opened while somebody is signed in never mixes the two accounts: the screen
 * says so and the activation only starts after that session is closed.
 */
export function SessionGate<M extends SessionMe>(props: SessionGateProps<M>) {
  return (
    <AuthLayoutContext.Provider value={props.layout ?? 'card'}>
      <Gate {...props} />
    </AuthLayoutContext.Provider>
  );
}

function Gate<M extends SessionMe>(props: SessionGateProps<M>) {
  const { api, app, router, appName, large, platform } = props;
  const state = useSessionState(api.session);
  const path = router.pathname;
  const params = new URLSearchParams(router.search);
  const next = safeNext(params.get('next'));

  // ---- public branding of this address (sign-in screens)
  const [hint] = useState(tenantHint);
  const [publicBrand, setPublicBrand] = useState<{ brand: AuthBrand; found: boolean; googleSignIn: boolean } | null>(null);
  useEffect(() => {
    void api
      .get<PublicBranding>(`/public/branding${hint ? `?tenant=${encodeURIComponent(hint)}` : ''}`)
      .then((r) => {
        if (!r.found || !r.branding) return setPublicBrand({ brand: { brandName: 'Despachador' }, found: false, googleSignIn: false });
        const b = r.branding as unknown as Branding & { tenantSlug: string; brandName: string };
        applyBranding(b, appName);
        props.onBranding?.(b);
        setPublicBrand({ brand: { brandName: b.brandName, logoUrl: b.logoUrl }, found: true, googleSignIn: r.googleSignIn === true });
      })
      .catch(() => setPublicBrand({ brand: { brandName: 'Despachador' }, found: false, googleSignIn: false }));
    void api.session.start();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const brand = publicBrand?.brand ?? { brandName: 'Despachador' };

  // ---- sign-in flow kept in memory across its routes (never in the URL or storage)
  const [identifier, setIdentifier] = useState('');
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [codes, setCodes] = useState<{ answer: Extract<SignInAnswer, { status: 'SIGNED_IN' }> } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  /** Token of an invitation link: in memory only (the apps take it out of the address bar at start-up). */
  const invitation = useSyncExternalStore(subscribeActivationToken, captureActivationToken);
  const [leaving, setLeaving] = useState(false);
  /** Google's return (taken out of the address bar at start-up) and an activation that went to Google. */
  const google = useSyncExternalStore(subscribeGoogleReturn, googleReturn);
  const trip = useSyncExternalStore(subscribeGoogleReturn, activationTrip);
  /** Password recovery kept in memory across its routes (never in the URL or storage). */
  const [recovery, setRecovery] = useState<RecoveryState>(() => emptyRecovery());

  const establish = (answer: Extract<SignInAnswer, { status: 'SIGNED_IN' }>) => {
    setChallenge(null);
    setCodes(null);
    setNotice(null);
    api.session.establish(answer);
  };
  const onAnswer = (answer: SignInAnswer, tenant: string | null) => {
    if (tenant && !hint) rememberTenant(tenant);
    if (answer.status === 'CHALLENGE') {
      setChallenge(answer);
      router.navigate(withNext(STEP, next));
    } else if (answer.recoveryCodes?.length) setCodes({ answer });
    else establish(answer);
  };
  const toStatus = (motive: StatusMotive, replace = false) => router.navigate(withNext(`${STATUS}${motive}`, next), { replace });
  const toSignIn = (replace = false) => router.navigate(withNext(SIGN_IN, next), { replace });

  // ---- the account behind a session
  const [me, setMe] = useState<{ epoch: number; data: M } | null>(null);
  const [meError, setMeError] = useState<string | null>(null);
  /** Where the next sign-out lands: the sign-in screen (the person chose it) or a status screen. */
  const explicitSignOut = useRef(false);
  const refusal = useRef<StatusMotive | null>(null);
  const signOut = useCallback(async () => {
    explicitSignOut.current = true;
    await api.session.signOut();
  }, [api]);
  const loadMe = useCallback(
    async (epoch: number) => {
      setMeError(null);
      try {
        const m = await api.get<M>('/auth/me');
        const refused = props.acceptAccount(m);
        if (refused) {
          // an account of the other app: no session here, and the screen says why
          refusal.current = refused;
          await api.session.signOut();
          return;
        }
        applyBranding(m.branding, appName);
        setFormatContext(m.tenant.locale, m.tenant.timezone);
        setMe({ epoch, data: m });
      } catch (err) {
        // a dead session moved the state already; anything else is shown with a retry
        if (api.session.state.status === 'signed-in') setMeError(errorText(err));
      }
    },
    [api], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const epoch = state.status === 'signed-in' ? state.epoch : null;
  useEffect(() => {
    if (epoch === null) {
      setMe(null);
      return;
    }
    if (me?.epoch !== epoch) void loadMe(epoch);
  }, [epoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- where the person should be, by state and path (navigation in effects: no loops, Back works)
  const inAccess = path.startsWith(ACCESS_PREFIX);
  const motive = path.startsWith(STATUS) ? path.slice(STATUS.length) : null;
  const recoveryStep = recoveryStepOf(path);
  const target: string | null = (() => {
    if (state.status === 'checking') return null;
    // an invitation link opened with a session: stay, the screen explains (never two accounts at once);
    // Google's return of a link, a re-authentication or a failure belongs to the open session
    if (state.status === 'signed-in')
      return inAccess && !(path === ACTIVATION_PATH && invitation) && !(path === GOOGLE_PATH && google && google.kind !== 'ticket') ? (next ?? '/') : null;
    // signed out
    if (inAccess) {
      if (path === SIGN_IN || path === ACTIVATION_PATH) return null;
      if (recoveryStep === 'start') return null;
      // the later steps need the flow in memory (a reload starts over); each step checks its own data
      if (recoveryStep === 'code' || recoveryStep === 'password') return recovery.alive ? null : RECOVERY_RESTART;
      if (recoveryStep === 'done') return recovery.alive ? null : SIGN_IN;
      // an activation that failed to link Google continues without it
      if (path === GOOGLE_PATH) return google?.kind === 'error' && trip ? ACTIVATION_PATH : google ? null : SIGN_IN;
      if (path === STEP) return challenge ? null : withNext(SIGN_IN, next);
      if (motive !== null) return isStatusMotive(motive) ? null : withNext(SIGN_IN, next);
      return withNext(SIGN_IN, next);
    }
    const here = `${path}${router.search}`;
    const keep = here === '/' ? null : here;
    if (refusal.current) return `${STATUS}${refusal.current}`;
    if (explicitSignOut.current) return SIGN_IN;
    const m = MOTIVE_OF_REASON[state.reason];
    return m ? withNext(`${STATUS}${m}`, keep) : withNext(SIGN_IN, keep);
  })();
  useEffect(() => {
    if (target !== null) router.navigate(target, { replace: true });
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  // the reasons of a sign-out are spent once its screen is reached (not when navigating: another state
  // change, e.g. the socket's 4401, may render again before the new location arrives)
  useEffect(() => {
    if (!inAccess) return;
    explicitSignOut.current = false;
    refusal.current = null;
  }, [inAccess]);
  // Google's return is spent once the landing is left (whatever the way out)
  useEffect(() => {
    if (google && path !== GOOGLE_PATH) clearGoogleReturn();
  }, [path, google]);

  // ---- rendering
  // plain on purpose: a reload of an open session passes here and must not flash the brand side
  const center = (label: string) => <AuthSpinner label={label} />;
  if (state.status === 'checking' || target !== null) return center('Verificando sesión');

  if (state.status === 'signed-out') {
    if (!publicBrand) return center('Cargando');
    if (path === ACTIVATION_PATH)
      return (
        <Activation
          key={invitation ?? (trip ? `trip-${trip.activationTicket.slice(-12)}` : 'none')}
          api={api}
          app={app}
          brand={brand}
          token={invitation}
          googleSignIn={publicBrand.googleSignIn}
          trip={invitation ? null : trip}
          platform={platform}
          large={large}
          onGesture={props.onSignInGesture}
          onSignedIn={(answer) => {
            clearActivationToken();
            clearActivationTrip();
            establish(answer);
          }}
          onSignIn={() => {
            clearActivationToken();
            clearActivationTrip();
            toSignIn();
          }}
        />
      );
    if (recoveryStep)
      return (
        <Recovery
          api={api}
          brand={brand}
          step={recoveryStep}
          state={recovery}
          onState={setRecovery}
          go={(step, opts) => router.navigate(RECOVERY_PATHS[step], opts)}
          askTenant={!publicBrand.found}
          tenant={hint}
          large={large}
          notice={recoveryStep === 'start' && params.get('reinicio') ? 'Por seguridad, la recuperación empieza de nuevo si recargas la página o sales de ella. Pide un código nuevo.' : null}
          onSignIn={() => {
            setIdentifier(recovery.identifier);
            setRecovery(emptyRecovery());
            toSignIn(true);
          }}
        />
      );
    if (codes)
      return (
        <AuthCard brand={brand} title="Verificación en 2 pasos activada" done>
          <RecoveryCodes codes={codes.answer.recoveryCodes!} doneLabel="Ingresar" large={large} onDone={() => establish(codes.answer)} />
        </AuthCard>
      );
    if (path === GOOGLE_PATH && google)
      return (
        <GoogleLanding
          api={api}
          brand={brand}
          ret={google}
          signedIn={false}
          platform={platform}
          large={large}
          onAnswer={(answer) => onAnswer(answer, null)}
          onRefused={(m) => toStatus(m, true)}
          onLeave={() => toSignIn(true)}
        />
      );
    if (path === STEP && challenge)
      return (
        <ChallengeStep
          key={challenge.challenge}
          api={api}
          brand={brand}
          challenge={challenge}
          large={large}
          onSignedIn={(answer) => (answer.recoveryCodes?.length ? setCodes({ answer }) : establish(answer))}
          onExpired={(message) => {
            setChallenge(null);
            setNotice(message);
            toSignIn(true);
          }}
          onRefused={(m) => {
            setChallenge(null);
            toStatus(m, true);
          }}
          onCancel={() => {
            setChallenge(null);
            setIdentifier('');
            toSignIn(true);
          }}
        />
      );
    if (motive !== null && isStatusMotive(motive))
      return (
        <StatusScreen
          motive={motive}
          app={app}
          brand={brand}
          large={large}
          busy={retrying}
          onSignIn={() => toSignIn()}
          onRetry={() => {
            setRetrying(true);
            void api.session.start().finally(() => setRetrying(false));
          }}
        />
      );
    return (
      <SignIn
        api={api}
        brand={brand}
        subtitle={props.subtitle}
        askTenant={!publicBrand.found}
        tenant={hint}
        platform={platform}
        large={large}
        identifier={identifier}
        onIdentifier={setIdentifier}
        notice={notice}
        onGesture={props.onSignInGesture}
        onAnswer={onAnswer}
        onRefused={(m) => toStatus(m)}
        onForgot={() => {
          setRecovery(emptyRecovery(identifier.trim()));
          router.navigate(RECOVERY_PATHS.start);
        }}
        googleHref={publicBrand.googleSignIn ? api.googleStartUrl(hint) : null}
      />
    );
  }

  // signed in: Google's return of a link or a re-authentication (or its failure)
  if (path === GOOGLE_PATH && google)
    return (
      <ReauthProvider api={api} allowGoogle={false}>
        <GoogleLanding
          api={api}
          brand={brand}
          ret={google}
          signedIn
          platform={platform}
          large={large}
          onAnswer={() => undefined}
          onRefused={() => undefined}
          onLeave={() => router.navigate(takeReturnPath(), { replace: true })}
        />
      </ReauthProvider>
    );

  // signed in
  if (path === ACTIVATION_PATH && invitation) {
    const who = me?.epoch === state.epoch ? me.data.user.fullName : null;
    return (
      <StatusCard
        brand={brand}
        title="Ya hay una sesión abierta"
        tone="warn"
        large={large}
        busy={leaving}
        action={{
          label: 'Cerrar esa sesión y activar la cuenta',
          busyLabel: 'Cerrando la sesión…',
          onClick: () => {
            setLeaving(true);
            // the invitation stays in memory: once signed out, this same route starts the activation
            void api.session.signOut().finally(() => setLeaving(false));
          },
        }}
        secondary={{
          label: 'Seguir con la sesión abierta',
          onClick: () => {
            clearActivationToken();
            router.navigate('/', { replace: true });
          },
        }}
      >
        En este navegador hay una sesión abierta{who ? ` a nombre de ${who}` : ''}. El enlace que abriste activa una cuenta: para que no se mezclen las dos, primero hay que cerrar esa sesión. Si la cuenta que vas a activar es
        de otra persona, es mejor que abra el enlace en su propio teléfono.
      </StatusCard>
    );
  }
  if (!me || me.epoch !== state.epoch) {
    if (meError)
      return (
        <div className="auth">
          <div className="card stack auth-card">
            <ErrorState message={`No pudimos cargar tu cuenta. ${meError}`} onRetry={() => void loadMe(state.epoch)} />
            <button type="button" className="btn block" onClick={() => void signOut()}>
              Cerrar sesión
            </button>
          </div>
        </div>
      );
    return center('Cargando tu cuenta');
  }
  return (
    <ReauthProvider api={api}>
      {props.children({
        me: me.data,
        reload: async () => setMe({ epoch: state.epoch, data: await api.get<M>('/auth/me') }),
        signOut,
      })}
    </ReauthProvider>
  );
}
